import { ITENS_POR_SETOR, SETORES } from "@caint/data";
import type { CicloPublico, EscopoData } from "@caint/shared";
import { SETORES_OBRIGATORIOS, comObrigatorios, migrarEscopo } from "@caint/shared";
import { useMemo, useState } from "react";
import { mensagemAmigavel } from "../lib/erros.ts";
import type { CiclosApi } from "../state/useAppState.ts";

const SETORES_TODOS = SETORES.map((s) => s.code);

function isoParaData(iso: string): string {
  return iso ? iso.slice(0, 10) : "";
}

export interface EditarCicloFormProps {
  ciclo: CicloPublico;
  ciclosApi: CiclosApi;
  /** Nome do ator (gestor) — registrado na trilha do escopo. */
  autor: string;
  /** Chamado após salvar com sucesso (recarrega a lista + estado). */
  onSaved: () => void | Promise<void>;
}

/**
 * Edição de um ciclo pela gestão (presidente/admin), na página de Ciclos:
 * estender prazo (datas), abrir/encerrar (status) e editar o escopo (setores) —
 * tudo **independente de o ciclo estar aberto ou não** (doc 08 §8.7 / doc 17b §5).
 * Presentational: o pai embrulha em Sheet (mobile) ou Modal (desktop).
 */
export function EditarCicloForm({ ciclo, ciclosApi, autor, onSaved }: EditarCicloFormProps) {
  const escopoBase = useMemo(() => migrarEscopo(ciclo.escopo, SETORES_TODOS), [ciclo.escopo]);
  const [inicio, setInicio] = useState(isoParaData(ciclo.dataInicio));
  const [fim, setFim] = useState(isoParaData(ciclo.dataFim));
  const [status, setStatus] = useState(ciclo.status);
  const [ativos, setAtivos] = useState<Set<string>>(() => new Set(escopoBase.setoresAtivos));
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  const nItens = useMemo(() => [...ativos].reduce((n, c) => n + (ITENS_POR_SETOR[c] ?? 0), 0), [ativos]);

  const alternar = (code: string) => {
    if (SETORES_OBRIGATORIOS.includes(code)) {
      return; // obrigatórios travados
    }
    setAtivos((prev) => {
      const proximo = new Set(prev);
      if (proximo.has(code)) {
        proximo.delete(code);
      } else {
        proximo.add(code);
      }
      return proximo;
    });
  };

  const salvar = async () => {
    setSalvando(true);
    setErro(null);
    try {
      // 1) Datas + status do ciclo.
      await ciclosApi.editar(ciclo.id, {
        dataInicio: new Date(`${inicio}T00:00:00.000Z`).toISOString(),
        dataFim: new Date(`${fim}T23:59:59.000Z`).toISOString(),
        status,
      });
      // 2) Escopo (se a avaliação existir) — gravado e revalidado no servidor.
      if (ciclo.avaliacaoId) {
        const escopo: EscopoData = {
          ...escopoBase,
          setoresAtivos: comObrigatorios(ativos),
          escopoConfirmado: true,
          escopoConfirmadoPor: autor,
          escopoConfirmadoEm: new Date().toISOString(),
        };
        await ciclosApi.salvarEscopo(ciclo.avaliacaoId, escopo);
      }
      await onSaved();
    } catch (e) {
      setErro(mensagemAmigavel(e));
      setSalvando(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {erro && <p style={{ color: "var(--danger)" }}>{erro}</p>}

      <div className="m-field">
        <label htmlFor="ec-ini">Início</label>
        <input
          id="ec-ini"
          type="date"
          className="m-input"
          value={inicio}
          onChange={(e) => setInicio(e.target.value)}
        />
      </div>
      <div className="m-field">
        <label htmlFor="ec-fim">Fim (prazo)</label>
        <input
          id="ec-fim"
          type="date"
          className="m-input"
          value={fim}
          onChange={(e) => setFim(e.target.value)}
        />
      </div>
      <div className="m-field">
        <label htmlFor="ec-status">Status</label>
        <select
          id="ec-status"
          className="m-select"
          value={status}
          onChange={(e) => setStatus(e.target.value as CicloPublico["status"])}
        >
          <option value="aberto">Aberto</option>
          <option value="encerrado">Encerrado</option>
        </select>
      </div>

      <div>
        <div
          style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}
        >
          <b style={{ fontSize: 13 }}>Setores da unidade (escopo)</b>
          <span className="spec">
            {ativos.size} setores · {nItens} itens
          </span>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 280, overflowY: "auto" }}>
          {SETORES.map((s) => {
            const on = ativos.has(s.code);
            const obrig = SETORES_OBRIGATORIOS.includes(s.code);
            return (
              <button
                key={s.code}
                type="button"
                className="m-card pad"
                disabled={obrig}
                onClick={() => alternar(s.code)}
                style={{
                  width: "100%",
                  textAlign: "left",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  cursor: obrig ? "default" : "pointer",
                  opacity: on ? 1 : 0.5,
                  border: on ? "1px solid var(--teal-300)" : "0.5px solid var(--border)",
                  background: "var(--surface)",
                  color: "var(--text)",
                }}
              >
                <span
                  aria-hidden
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: 5,
                    flexShrink: 0,
                    display: "grid",
                    placeItems: "center",
                    fontSize: 13,
                    background: on ? "var(--teal-500, var(--teal-300))" : "transparent",
                    border: on ? "none" : "1.5px solid var(--border)",
                    color: "#fff",
                  }}
                >
                  {on ? "✓" : ""}
                </span>
                <span style={{ flex: 1, fontSize: 13.5 }}>
                  {s.nome}
                  <span className="spec" style={{ display: "block" }}>
                    {s.code} · {ITENS_POR_SETOR[s.code] ?? 0} itens{obrig ? " · obrigatório 🔒" : ""}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <button
        type="button"
        className="m-btn primary"
        disabled={salvando || !inicio || !fim}
        onClick={() => void salvar()}
      >
        {salvando ? "Salvando…" : "Salvar alterações"}
      </button>
    </div>
  );
}
